// Shared-tablet code: a short, readable code derived from the device id
// (talleres/{t}/dispositivos/{id}) so the admin can tell that the tablet
// waiting in the backoffice (Equipos) is the one in front of them. No new
// state: the app and the backoffice compute it from the id alone.
// Mirrored in Dart (apps/inspeccion/lib/features/ingreso/datos/codigo_tablet.dart);
// both reproduce shared/src/tablet.vectores.json.

/** 32 symbols without the look-alikes 0/O and 1/I. */
export const ALFABETO_CODIGO_TABLET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

/** Typed-array read without a non-null assertion (indices are always in range). */
const en = (a: Uint32Array, i: number) => a[i] ?? 0;

/**
 * SHA-256 of [datos] (FIPS 180-4). Synchronous on purpose: Web Crypto is
 * async only, and the code is rendered inline in a list.
 */
export function sha256(datos: Uint8Array): Uint8Array {
  const largo = datos.length;
  const bloques = Math.ceil((largo + 9) / 64);
  const m = new Uint8Array(bloques * 64);
  m.set(datos);
  m[largo] = 0x80;
  const vista = new DataView(m.buffer);
  // Bit length as a 64-bit big-endian integer.
  vista.setUint32(m.length - 8, Math.floor(largo / 0x20000000));
  vista.setUint32(m.length - 4, (largo * 8) >>> 0);
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  for (let b = 0; b < bloques; b++) {
    for (let i = 0; i < 16; i++) w[i] = vista.getUint32(b * 64 + i * 4);
    for (let i = 16; i < 64; i++) {
      const w15 = en(w, i - 15);
      const w2 = en(w, i - 2);
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
      w[i] = (en(w, i - 16) + s0 + en(w, i - 7) + s1) >>> 0;
    }
    let a = en(h, 0);
    let bb = en(h, 1);
    let c = en(h, 2);
    let d = en(h, 3);
    let e = en(h, 4);
    let f = en(h, 5);
    let g = en(h, 6);
    let hh = en(h, 7);
    for (let i = 0; i < 64; i++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + s1 + ch + en(K, i) + en(w, i)) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & bb) ^ (a & c) ^ (bb & c);
      const t2 = (s0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = bb;
      bb = a;
      a = (t1 + t2) >>> 0;
    }
    [a, bb, c, d, e, f, g, hh].forEach((x, i) => (h[i] = en(h, i) + x));
  }
  const salida = new Uint8Array(32);
  const vs = new DataView(salida.buffer);
  h.forEach((x, i) => vs.setUint32(i * 4, x));
  return salida;
}

/**
 * The tablet code: the first 30 bits of SHA-256(UTF-8 id) as 6 symbols of
 * [ALFABETO_CODIGO_TABLET], shown in two groups: "4F7 · K2Q".
 */
export function codigoTablet(dispositivoId: string): string {
  const d = sha256(new TextEncoder().encode(dispositivoId));
  // 32 bits big-endian; the top 30 make 6 symbols of 5 bits.
  const n = new DataView(d.buffer).getUint32(0);
  let s = '';
  for (let i = 0; i < 6; i++) s += ALFABETO_CODIGO_TABLET[(n >>> (27 - 5 * i)) & 31];
  return `${s.slice(0, 3)} · ${s.slice(3)}`;
}
